import {
  WIDGET_SIZE_PRESETS,
  WidgetSize,
  getViewportConstrainedMaxHeight,
} from '@automation-suite/shared-clients-widget-variant';
import { Anchor } from '../types/Anchor';

const DESKTOP_HEIGHT_WITH_INTENTS = '180px';
const DESKTOP_HEIGHT_NO_INTENTS = '85px';

const MOBILE_HEIGHT_WITH_INTENTS = '160px';
const MOBILE_HEIGHT_NO_INTENTS = '85px';

const FIC_HORIZONTAL_PADDING = '32px';

export const defaultIframeStyles = {
  border: 'none',
  'box-sizing': 'border-box',
  position: 'fixed',
  'z-index': '99999',
  backgroundColor: 'transparent',
};

const desktopDefaultStyles = {
  bottom: '0px',
  display: 'block',
  left: 'unset',
  maxHeight: '90%',
  maxWidth: '100%',
  right: '0px',
  width: '520px',
  transform: 'none',
};

export type GetStylesParams = {
  anchorX?: Anchor;
  offsetXpx?: number;
  offsetYpx?: number;
  size?: WidgetSize;
  withIntents?: boolean;
};

const getDefaultHorizontalStyles = (anchorX: Anchor, offsetXpx: number) => {
  switch (anchorX) {
    case 'center':
      return {
        left: '50%',
        right: 'auto',
        transform: `translateX(calc(-50% - ${offsetXpx}px))`,
      };
    case 'left':
      return {
        left: `calc(${offsetXpx}px + ${FIC_HORIZONTAL_PADDING})`,
        right: 'auto',
        transform: 'none',
      };
    case 'right':
    default:
      return {
        right: `calc(${offsetXpx}px + ${FIC_HORIZONTAL_PADDING})`,
        left: 'auto',
        transform: 'none',
      };
  }
};

const getDesktopSupportWindowHorizontalStyles = (anchorX: Anchor) => {
  switch (anchorX) {
    case 'left':
      return {
        left: '16px',
        right: 'unset',
      };
    case 'center':
    case 'right':
    default:
      return {
        left: 'unset',
        right: '16px',
      };
  }
};

export const getDesktopDefaultStyles = ({
  anchorX = 'right',
  offsetXpx = 0,
  offsetYpx = 0,
  withIntents = true,
}: GetStylesParams) => {
  const height = withIntents ? DESKTOP_HEIGHT_WITH_INTENTS : DESKTOP_HEIGHT_NO_INTENTS;
  const horizontalStyles = getDefaultHorizontalStyles(anchorX, offsetXpx);

  return {
    ...desktopDefaultStyles,
    height,
    bottom: `calc(${desktopDefaultStyles.bottom} + ${offsetYpx}px)`,
    maxHeight: `calc(${desktopDefaultStyles.maxHeight} - ${offsetYpx}px)`,
    maxWidth: `calc(${desktopDefaultStyles.maxWidth} - ${offsetXpx}px)`,
    ...horizontalStyles,
  };
};

const desktopSupportWindowStyles = {
  bottom: '16px',
  display: 'block',
  height: '100%',
  maxHeight: `calc(100% - 32px)`,
  maxWidth: '100%',
  width: '668px',
  transform: 'none',
};

export const getDesktopSupportWindowStyles = ({ anchorX = 'right', size = 'large' }: GetStylesParams) => {
  const sizePreset = WIDGET_SIZE_PRESETS[size];
  const horizontalStyles = getDesktopSupportWindowHorizontalStyles(anchorX);

  return {
    ...desktopSupportWindowStyles,
    width: sizePreset.width,
    maxHeight: getViewportConstrainedMaxHeight(size),
    bottom: desktopSupportWindowStyles.bottom,
    ...horizontalStyles,
  };
};

const mobileSupportWindowStyles = {
  bottom: '0px',
  display: 'block',
  height: '100%',
  left: '0px',
  maxHeight: '100%',
  maxWidth: '100%',
  right: '0px',
  width: '100%',
  transform: 'none',
};

export const getMobileSupportWindowStyles = () => ({
  ...mobileSupportWindowStyles,
  bottom: mobileSupportWindowStyles.bottom,
  right: mobileSupportWindowStyles.right,
  maxHeight: mobileSupportWindowStyles.maxHeight,
  maxWidth: mobileSupportWindowStyles.maxWidth,
});

const mobileDefaultStyles = {
  bottom: '0px',
  display: 'block',
  left: '0px',
  maxHeight: '100%',
  maxWidth: '100%',
  right: '0px',
  width: '100%',
  transform: 'none',
};

export const getMobileDefaultStyles = ({ offsetYpx = 0, offsetXpx = 0, withIntents = true }: GetStylesParams) => {
  const height = withIntents ? MOBILE_HEIGHT_WITH_INTENTS : MOBILE_HEIGHT_NO_INTENTS;

  return {
    ...mobileDefaultStyles,
    height,
    bottom: `calc(${mobileDefaultStyles.bottom} + ${offsetYpx}px)`,
    right: `calc(${mobileDefaultStyles.right} + ${offsetXpx}px)`,
    maxHeight: `calc(${mobileDefaultStyles.maxHeight} - ${offsetYpx}px)`,
    maxWidth: `calc(${mobileDefaultStyles.maxWidth} - ${offsetXpx}px)`,
    backgroundColor: '#ffffff',
  };
};

export const collapsedStyles = {
  display: 'block',
  height: '110px',
  width: '110px',
  maxHeight: '110px',
  maxWidth: '110px',
  left: 'unset',
};
